import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import {
  Code as CodeIcon,
  ChatBubble as ChatIcon,
  Forum as ForumIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useViewMode } from '../contexts/ViewModeContext';
import { useSystemConfig } from '../hooks/useSystemConfig';

interface FirstLoginModalProps {
  open: boolean;
}

export const FirstLoginModal: React.FC<FirstLoginModalProps> = ({ open }) => {
  const { setIntendedUse, setViewMode, completeOnboarding } = useViewMode();
  const navigate = useNavigate();
  const guideOn = useSystemConfig().features.build_guide;
  const [selectedUse, setSelectedUse] = useState<string>('');

  const useCases = [
    ...(guideOn
      ? [
          {
            value: 'guided',
            title: 'Not sure yet? Describe your idea',
            description:
              "Recommended if you're new. Tell the Build Guide what you want in plain words; it suggests the right setup and opens the builder with everything filled in.",
            icon: <ForumIcon sx={{ fontSize: 48 }} />,
            color: '#4f46e5',
            viewMode: 'developer' as const,
            features: ['No technical knowledge needed', 'Takes a minute', 'You can switch workspaces anytime'],
            path: '/dashboard/guide',
          },
        ]
      : []),
    {
      value: 'developer',
      title: 'Developer / API Integration',
      description: 'For app and website developers who want full control. Build custom AI functions, manage data structures, integrate APIs, and access advanced tools.',
      icon: <CodeIcon sx={{ fontSize: 48 }} />,
      color: '#1976d2',
      viewMode: 'developer' as const,
      features: ['Full API access', 'Custom data structures', 'Advanced builder', 'Code examples', 'API documentation']
    },
    {
      value: 'chatbot_creator',
      title: 'Chatbot Creator',
      description: 'For businesses creating AI chatbots. Simple interface to build, brand, and deploy conversational AI agents for your website or customer service.',
      icon: <ChatIcon sx={{ fontSize: 48 }} />,
      color: '#9c27b0',
      viewMode: 'chatbot_generation' as const,
      features: ['Easy chatbot builder', 'Custom branding', 'Downloadable HTML', 'Knowledge base', 'One-click deploy']
    },
  ];

  const handleSelectUseCase = async (value: string) => {
    const selected = useCases.find(uc => uc.value === value);
    if (!selected) return;
    
    setSelectedUse(value);
    
    // Save intended use and set view mode
    await setIntendedUse(value);
    setViewMode(selected.viewMode);
    
    // Mark to show view switcher tutorial
    localStorage.setItem('shouldShowViewSwitcherTutorial', 'true');
    
    // Complete onboarding immediately
    await completeOnboarding();
    if ('path' in selected && selected.path) navigate(selected.path);
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
        }
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <RocketIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
              Welcome to AI Platform!
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1.1rem' }}>
              Choose your experience - click to get started!
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {useCases.map((useCase) => (
              <Card
                key={useCase.value}
                sx={{
                  cursor: 'pointer',
                  border: selectedUse === useCase.value ? `3px solid ${useCase.color}` : '2px solid #e0e0e0',
                  bgcolor: selectedUse === useCase.value ? alpha(useCase.color, 0.05) : 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: useCase.color,
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  }
                }}
                onClick={() => handleSelectUseCase(useCase.value)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ 
                      color: useCase.color, 
                      mr: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: 2,
                      bgcolor: alpha(useCase.color, 0.1),
                    }}>
                      {useCase.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {useCase.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {useCase.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {useCase.features.map((feature) => (
                          <Typography 
                            key={feature}
                            variant="caption" 
                            sx={{ 
                              px: 1, 
                              py: 0.25, 
                              bgcolor: alpha(useCase.color, 0.1),
                              color: useCase.color,
                              borderRadius: 1,
                              fontWeight: 500,
                              fontSize: '0.7rem'
                            }}
                          >
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

